import { ListItemNode, ListNode } from '@lexical/list';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import {
  type DOMExportOutput,
  type DOMExportOutputMap,
  type Klass,
  type LexicalEditor,
  type LexicalNode,
  ParagraphNode,
  TextNode,
  isHTMLElement,
} from 'lexical';

import { CustomListItemNode } from '../nodes/CustomListItemNode';
import { CustomListNode } from '../nodes/CustomListNode';

/**
 * Removes class attributes and empty style attributes from exported HTML elements.
 * Preserves all inline styles (text-align, colors, etc.).
 */
export const removeStylesExportDOM = (
  editor: LexicalEditor,
  target: LexicalNode,
): DOMExportOutput => {
  const output = target.exportDOM(editor);
  if (output && isHTMLElement(output.element)) {
    const originalAfter = output.after;
    output.after = (generatedElement) => {
      const result = originalAfter?.(generatedElement);
      const finalElement = result || generatedElement;

      if (finalElement && isHTMLElement(finalElement)) {
        // Collect all elements including the root and all descendants
        const allElements: HTMLElement[] = [finalElement];
        const descendants = finalElement.querySelectorAll('*');
        for (const el of descendants) {
          if (isHTMLElement(el)) {
            allElements.push(el);
          }
        }

        // Remove class attributes and empty style attributes
        for (const el of allElements) {
          el.removeAttribute('class');
          const styleAttr = el.getAttribute('style');
          if (!styleAttr || styleAttr.trim() === '') {
            el.removeAttribute('style');
          }
        }
      }

      return finalElement;
    };
  }
  return output;
};

export const exportMap: DOMExportOutputMap = new Map<
  Klass<LexicalNode>,
  (editor: LexicalEditor, target: LexicalNode) => DOMExportOutput
>([
  [ParagraphNode, removeStylesExportDOM],
  [TextNode, removeStylesExportDOM],
  [HeadingNode, removeStylesExportDOM],
  [QuoteNode, removeStylesExportDOM],
  [ListNode, removeStylesExportDOM],
  [ListItemNode, removeStylesExportDOM],
  [CustomListNode, removeStylesExportDOM],
  [CustomListItemNode, removeStylesExportDOM],
]);
